import { Component } from '@theme/component';

/**
 * @typedef {Object} CollectionTabsRefs
 * @property {HTMLElement[]} tabs
 */

/** @extends {Component<CollectionTabsRefs>} */
export class CollectionTabsComponent extends Component {
  requiredRefs = ['tabs'];
  #active = 0;
  /** @type {AbortController | null} */
  #abort = null;

  connectedCallback() {
    super.connectedCallback();
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    this.#active = Math.max(
      0,
      tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true')
    );

    this.#setupEventListeners();
    this.#updateActiveTab();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
  }

  #setupEventListeners() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const opts = { signal: this.#abort.signal };
    const { tabs } = this.refs;

    this.addEventListener('keydown', (e) => this.#handleKeydown(e), opts);

    for (const [i, tab] of tabs.entries()) {
      tab.addEventListener('click', (e) => this.#handleTabClick(e, i), opts);
      tab.addEventListener('focus', () => this.#handleTabFocus(i), opts);
    }
  }

  #handleKeydown(e) {
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    let targetIndex = this.#active;

    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        targetIndex = this.#active > 0 ? this.#active - 1 : tabs.length - 1;
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        targetIndex = this.#active < tabs.length - 1 ? this.#active + 1 : 0;
        break;
      case 'Home':
        e.preventDefault();
        targetIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        targetIndex = tabs.length - 1;
        break;
      default:
        return;
    }

    this.#switchTab(targetIndex);
    tabs[targetIndex]?.focus();
  }

  #handleTabClick(e, index) {
    e.preventDefault();
    this.#switchTab(index);
  }

  #handleTabFocus(index) {
    this.#switchTab(index);
  }

  #switchTab(index) {
    if (index === this.#active || index < 0) return;

    const { tabs } = this.refs;
    if (!tabs?.[index]) return;

    this.#active = index;
    this.#updateActiveTab();
  }

  #updateActiveTab() {
    const { tabs } = this.refs;
    if (!tabs?.length) return;

    const panels = this.querySelectorAll('.collection-tabs__panel');
    if (!panels.length) return;

    for (const [i, tab] of tabs.entries()) {
      const isActive = i === this.#active;
      const panel = panels[i];

      // タブの状態を更新
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
      tab.classList.toggle('collection-tabs__tab--active', isActive);

      // パネルの状態を更新
      if (panel) {
        if (isActive) {
          panel.classList.remove('collection-tabs__panel--hidden');
          panel.classList.add('collection-tabs__panel--active');
          panel.removeAttribute('inert');
        } else {
          panel.classList.add('collection-tabs__panel--hidden');
          panel.classList.remove('collection-tabs__panel--active');
          panel.setAttribute('inert', '');
        }
      }
    }
  }
}

customElements.define('collection-tabs-component', CollectionTabsComponent);
